import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Per-page SEO helper. Injects <title>, meta description, Open Graph,
 * Twitter card, canonical link and optional JSON-LD schema. Use it once
 * per page near the top of the JSX tree.
 *
 * Props:
 *  - title: full <title> tag content (we already include the brand suffix in callers)
 *  - description: meta description (140-160 chars sweet spot)
 *  - path: optional path override for canonical/og:url (defaults to current location)
 *  - image: optional absolute or relative og:image URL (default = /og-image.png)
 *  - type: og:type (default "website")
 *  - jsonLd: array of JSON-LD objects to embed as <script type="application/ld+json">
 *  - noindex: boolean — when true emits a robots noindex directive
 */
export default function SEO({
  title,
  description,
  path,
  image,
  type = "website",
  jsonLd = [],
  noindex = false,
}) {
  const origin = typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://whats-included-1.preview.emergentagent.com";
  const currentPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const url = `${origin}${currentPath}`;
  const imageUrl = (() => {
    const fallback = "/og-image.png";
    const resolved = image || fallback;
    if (/^https?:\/\//.test(resolved)) return resolved;
    return `${origin}${resolved.startsWith("/") ? "" : "/"}${resolved}`;
  })();

  const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Canonical */}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Ever Clean Home" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />

      {/* JSON-LD blocks */}
      {blocks.filter(Boolean).map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
